/**
 * @description React hook for real-time notification delivery via WebSocket.
 * Connects to `/ws/notifications` and automatically invalidates React Query
 * caches when a new notification arrives. Uses a shared singleton socket with
 * reference counting and exponential backoff reconnection.
 *
 * @example
 * function NotificationBell() {
 *   const { isConnected } = useNotificationSocket();
 *   // Notifications and unread counts are auto-invalidated in React Query
 * }
 *
 * @returns {{ isConnected: boolean }} Whether the WebSocket is currently connected
 */
import { useEffect, useState, useRef, useCallback } from "react";
import { queryClient } from "@/lib/queryClient";

/** Event pushed from the server when a new notification is created */
interface NotificationPushEvent {
  type: "new_notification";
  notification: Record<string, unknown>;
  unreadCount: number;
}

/** Connected confirmation from the server */
interface ConnectedEvent {
  type: "connected";
  userId: string;
}

type ServerEvent = NotificationPushEvent | ConnectedEvent | { type: "pong" };

// ── Shared singleton socket (survives across component mounts) ──────────

let sharedSocket: WebSocket | null = null;
let refCount = 0;
let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempt = 0;
let currentlyConnected = false;

const MAX_RECONNECT_DELAY_MS = 30_000;
const BASE_RECONNECT_DELAY_MS = 1_000;

const connectionListeners = new Set<(connected: boolean) => void>();
const messageListeners = new Set<(event: ServerEvent) => void>();

function notifyConnectionChange(connected: boolean) {
  currentlyConnected = connected;
  Array.from(connectionListeners).forEach((listener) => {
    listener(connected);
  });
}

function getWsUrl(): string {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}/ws/notifications`;
}

function clearReconnect(): void {
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }
}

function scheduleReconnect(): void {
  clearReconnect();
  if (refCount <= 0) return;

  // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s (capped)
  const delay = Math.min(
    BASE_RECONNECT_DELAY_MS * Math.pow(2, reconnectAttempt),
    MAX_RECONNECT_DELAY_MS
  );
  reconnectAttempt++;

  reconnectTimeout = setTimeout(connect, delay);
}

function connect(): void {
  if (
    sharedSocket?.readyState === WebSocket.OPEN ||
    sharedSocket?.readyState === WebSocket.CONNECTING
  ) {
    return;
  }

  clearReconnect();

  try {
    sharedSocket = new WebSocket(getWsUrl());
  } catch {
    scheduleReconnect();
    return;
  }

  sharedSocket.onopen = () => {
    clearReconnect();
    reconnectAttempt = 0;
    notifyConnectionChange(true);
  };

  sharedSocket.onmessage = (event) => {
    try {
      const data: ServerEvent = JSON.parse(event.data);
      Array.from(messageListeners).forEach((listener) => {
        listener(data);
      });
    } catch {
      // Ignore malformed messages
    }
  };

  sharedSocket.onclose = () => {
    sharedSocket = null;
    notifyConnectionChange(false);
    if (refCount > 0) {
      scheduleReconnect();
    }
  };

  sharedSocket.onerror = () => {
    // Will trigger onclose which handles reconnect
    sharedSocket?.close();
  };
}

function addRef(): void {
  refCount++;
  if (refCount === 1) {
    connect();
  }
}

function removeRef(): void {
  refCount = Math.max(0, refCount - 1);
  if (refCount === 0) {
    clearReconnect();
    reconnectAttempt = 0;
    sharedSocket?.close();
    sharedSocket = null;
    notifyConnectionChange(false);
    // No active subscribers — release the document-level visibility listener too.
    detachVisibilityHandler();
  }
}

// ── Visibility handler: pause/resume on tab visibility ──────────────

let visibilityHandler: (() => void) | null = null;

function attachVisibilityHandler(): void {
  if (visibilityHandler) return;
  visibilityHandler = () => {
    if (document.visibilityState === "visible" && refCount > 0) {
      // Tab became visible — reconnect if disconnected
      if (!sharedSocket || sharedSocket.readyState !== WebSocket.OPEN) {
        reconnectAttempt = 0;
        connect();
      }
    }
  };
  document.addEventListener("visibilitychange", visibilityHandler);
}

function detachVisibilityHandler(): void {
  if (!visibilityHandler) return;
  document.removeEventListener("visibilitychange", visibilityHandler);
  visibilityHandler = null;
}

// ── Hook ────────────────────────────────────────────────────────────

export function useNotificationSocket(): { isConnected: boolean } {
  const [isConnected, setIsConnected] = useState(currentlyConnected);
  const isConnectedRef = useRef(currentlyConnected);

  // Handle connection state changes
  const onConnectionChange = useCallback((connected: boolean) => {
    isConnectedRef.current = connected;
    setIsConnected(connected);
  }, []);

  // Handle incoming messages — invalidate React Query caches
  const onMessage = useCallback((event: ServerEvent) => {
    if (event.type === "new_notification") {
      // Invalidate both the notification list and unread count
      queryClient.invalidateQueries({
        queryKey: ["/api/notifications"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/notifications/unread-count"],
      });
    }
  }, []);

  useEffect(() => {
    attachVisibilityHandler();

    connectionListeners.add(onConnectionChange);
    messageListeners.add(onMessage);
    addRef();

    // Sync initial state
    setIsConnected(currentlyConnected);

    return () => {
      connectionListeners.delete(onConnectionChange);
      messageListeners.delete(onMessage);
      removeRef();
    };
  }, [onConnectionChange, onMessage]);

  return { isConnected };
}
